/**
 * Trinkets (docs/trinkets.md, flag `trinketsUI`; owner, 2026-10-11: "Add a new feature called
 * trinkets, you can collect trinkets that are viewable in 3d and play music like in pikmin").
 *
 * While a peon does a chore they can turn up a trinket in the mess: a lost button, a bottle cap,
 * one of Omie's tennis balls. The household keeps them in one collection, the trinkets screen,
 * where each one can be turned around in 3D (a little voxel model, shared/voxel.ts) and plays its
 * own tune while it is looked at, the way Pikmin's treasure hoard does. Each is found once per
 * household, pays its finder a little gold and is kept for good.
 *
 * Finding and paying never depend on the flag (docs/roadmap-2026-10.md: "Data never depends on a
 * UI flag"); the flag shows the screen, the find sheet, the Profile row and lets the push go out.
 *
 * The rules are pure functions over what the server reads at the moment of a completion
 * (server/trinkets.ts). Shared by the server, the web and the native app.
 */
import { DISH_WORDS, LAUNDRY_WORDS, isOmieJob, titleMentions } from "./achievements.ts";

/** Every trinket's key, in the order the collection shows them. New trinkets go on the end. */
export const TRINKET_KEYS = [
  "lucky-spoon",
  "fancy-teacup",
  "odd-sock",
  "lost-button",
  "tennis-ball",
  "squeaky-bone",
  "rubber-duck",
  "garden-gnome",
  "tv-remote",
  "toy-brick",
  "fridge-magnet",
  "whole-cookie",
  "bottle-cap",
  "green-marble",
  "old-key",
  "shiny-coin",
  "orange-pudding",
  "purple-orange",
  "potato-salad",
  "fridge-shawarma",
  "omies-confetti",
  "lenny-nuggets",
  "diet-coke-can",
  "game-cube",
  "omies-sock",
] as const;
export type TrinketKey = (typeof TRINKET_KEYS)[number];

/**
 * Where a trinket turns up: the kind of chore it hides in. A job's title names its pools
 * (poolsFor); "anywhere" is every job. A pool's trinkets come up first, then anywhere's, then
 * whatever is left, so a house that never does yard work still finds the gnome in the end.
 */
export type TrinketPool = "dishes" | "laundry" | "omie" | "bathroom" | "yard" | "living" | "kitchen" | "anywhere";

/** Title words for each pool but dishes, laundry and Omie (those are the badges' own lists). Lowercase, matched anywhere. */
export const POOL_WORDS: Record<Exclude<TrinketPool, "dishes" | "laundry" | "omie" | "anywhere">, readonly string[]> = {
  bathroom: ["bathroom", "bath", "shower", "toilet", "tub", "mirror"],
  yard: ["lawn", "garden", "weed", "rake", "mow", "leaves", "yard", "shovel", "snow", "hedge", "plant", "deck", "patio"],
  living: ["vacuum", "couch", "sofa", "cushion", "living room", "tidy", "dust", "rug", "carpet", "tv", "shelf", "shelves"],
  kitchen: ["kitchen", "fridge", "counter", "stove", "oven", "grocer", "pantry", "microwave", "table", "compost", "garbage", "trash", "bins"],
};

export interface Trinket {
  key: TrinketKey;
  /**
   * The trinket's name, shown and pushed. A common thing named the way an explorer from far away
   * would name it, like Pikmin's treasures (owner, 2026-10-14: "funny names like in pikmin, common
   * objects with weird names as though they're alien objects"). ("Trinket: Soup Shovel".)
   */
  name: string;
  /** One line about it, the collection's label: the explorer's field note, guessing at what the natives use it for. Never cruel. */
  line: string;
  /** The gold it pays its finder once: 5 for a common one, 10 for an uncommon, 20 for a rare. */
  gold: number;
  /** The chores it hides in. */
  pool: TrinketPool;
}

export const TRINKETS: readonly Trinket[] = [
  { key: "lucky-spoon", name: "Soup Shovel", line: "Moves soup into a native's face. Found in the wet cave.", gold: 5, pool: "dishes" },
  { key: "fancy-teacup", name: "Thirst Chalice", line: "Too sacred to drink from. The natives only look at it.", gold: 20, pool: "dishes" },
  { key: "odd-sock", name: "Lonely Foot Hat", line: "A hat for one foot. The spinning box took its partner.", gold: 5, pool: "laundry" },
  { key: "lost-button", name: "Shirt Pebble", line: "Holds the natives' coverings shut. This one escaped.", gold: 5, pool: "laundry" },
  { key: "tennis-ball", name: "Drool Moon", line: "A fuzzy moon. The big furry one wants it back.", gold: 5, pool: "omie" },
  { key: "squeaky-bone", name: "Honking Relic", line: "Squeeze it and it honks. The big furry one approves.", gold: 10, pool: "omie" },
  { key: "rubber-duck", name: "Quiet Bath Sage", line: "Floats in the hot pond and watches. Has never said a word.", gold: 10, pool: "bathroom" },
  { key: "garden-gnome", name: "Lawn Elder", line: "A small bearded keeper of the grass. Never moves.", gold: 20, pool: "yard" },
  { key: "tv-remote", name: "Channel Wand", line: "Changes the glowing window. Lives in the couch.", gold: 10, pool: "living" },
  { key: "toy-brick", name: "Ouch Cube", line: "Hides on the floor and waits for bare feet.", gold: 5, pool: "living" },
  { key: "fridge-magnet", name: "Memory Tile", line: "Clings to the cold box. Remembers a trip nobody does.", gold: 10, pool: "kitchen" },
  { key: "whole-cookie", name: "Crumbly Sun Disc", line: "Not one bite taken. The natives never allow this.", gold: 5, pool: "kitchen" },
  { key: "bottle-cap", name: "Crinkled Crown", line: "A tiny crown for a bottle. Bent from royal duties.", gold: 5, pool: "anywhere" },
  { key: "green-marble", name: "Green Gaze Orb", line: "A small green planet. Rolled under the couch long ago.", gold: 5, pool: "anywhere" },
  { key: "old-key", name: "Door Whisperer", line: "Speaks to doors. No door here remembers its voice.", gold: 10, pool: "anywhere" },
  { key: "shiny-coin", name: "Pocket Sun", line: "Shinier than gold. The natives trade them for snacks.", gold: 20, pool: "anywhere" },
  // Nine more from the owner (2026-10-15).
  { key: "orange-pudding", name: "Wobbly Sun Cup", line: "Orange and wobbly. Trembles when the cold box opens.", gold: 5, pool: "kitchen" },
  { key: "purple-orange", name: "Confused Citrus", line: "The natives swear it is an orange. It is purple.", gold: 10, pool: "kitchen" },
  { key: "potato-salad", name: "Picnic Mush Mound", line: "Potato lumps in goo. Brought to every gathering, uneaten.", gold: 5, pool: "kitchen" },
  { key: "fridge-shawarma", name: "Rotted Cuisine", line: "Meat rolled in bread, forgotten at the back of the cold box.", gold: 20, pool: "kitchen" },
  { key: "omies-confetti", name: "Party Scraps", line: "The big furry one shreds anything into a big celebration.", gold: 5, pool: "living" },
  { key: "lenny-nuggets", name: "Surprise Pebbles", line: "Lenny leaves these everywhere. A gift. Do not touch.", gold: 10, pool: "omie" },
  { key: "diet-coke-can", name: "Nectar Capsule", line: "Silver, red band, fizzing. Someone was saving it.", gold: 10, pool: "anywhere" },
  { key: "game-cube", name: "Gaming Cube", line: "It appears to be a “gaming” machine. The functional purpose of the cube shape is unknown.", gold: 20, pool: "living" },
  { key: "omies-sock", name: "Stolen Foot Hat", line: "The big furry one's treasure. Damp, stretched, his now.", gold: 10, pool: "omie" },
];

/** How many trinkets there are to find. */
export const TRINKET_TOTAL = TRINKETS.length;

const BY_KEY: ReadonlyMap<string, Trinket> = new Map(TRINKETS.map((t) => [t.key, t]));

export function trinket(key: string): Trinket | undefined {
  return BY_KEY.get(key);
}

export function isTrinketKey(x: unknown): x is TrinketKey {
  return typeof x === "string" && BY_KEY.has(x);
}

// ---------- Finding ----------

/** The chance a completion turns up a trinket, when the pity rule does not decide it. */
export const FIND_CHANCE = 0.2;
/**
 * The pity rule: after this many completions in a row without a find, the next one always finds
 * (while anything is left to find), so a quiet house still sees a trinket now and then.
 */
export const PITY_MISSES = 7;

/**
 * Whether a completion finds a trinket. `sinceLastFind` counts the household's completions since
 * its last find, this one included (1 is the first completion after a find).
 */
export function shouldFind(sinceLastFind: number, random: () => number = Math.random): boolean {
  if (sinceLastFind > PITY_MISSES) return true;
  return random() < FIND_CHANCE;
}

/** The pools a job's title names, in the catalogue's order of preference, "anywhere" last. */
export function poolsFor(title: string): TrinketPool[] {
  const pools: TrinketPool[] = [];
  if (titleMentions(DISH_WORDS, title)) pools.push("dishes");
  if (titleMentions(LAUNDRY_WORDS, title)) pools.push("laundry");
  if (isOmieJob(title)) pools.push("omie");
  for (const pool of Object.keys(POOL_WORDS) as (keyof typeof POOL_WORDS)[]) {
    if (titleMentions(POOL_WORDS[pool], title)) pools.push(pool);
  }
  pools.push("anywhere");
  return pools;
}

/**
 * The trinket a job turns up, given what the household has found already: one not yet found from
 * the job's own pools, else from anywhere, else any left at all; null once everything is found.
 * `random` picks among the candidates (uniform in [0, 1)).
 */
export function pickTrinket(found: ReadonlyArray<string>, title: string | null, random: () => number = Math.random): Trinket | null {
  const have = new Set(found);
  const left = TRINKETS.filter((t) => !have.has(t.key));
  if (left.length === 0) return null;
  const pools = poolsFor(title ?? "");
  let candidates: Trinket[] = [];
  for (const pool of pools) {
    candidates = left.filter((t) => t.pool === pool);
    if (candidates.length > 0) break;
  }
  if (candidates.length === 0) candidates = left;
  return candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
}

// ---------- What the server answers ----------

/** One found trinket, as GET /api/trinkets lists it and as a completion carries it. */
export interface TrinketFind {
  key: TrinketKey;
  /** Who found it, as the name was stored. */
  found_by: string;
  /** ISO-8601 UTC. */
  found_at: string;
  /** The task it was found in; null for one dug up from the workshop. */
  task_id: string | null;
  /** That task's title, joined on read; null when there was no task or it is gone. */
  task_title: string | null;
}

export interface TrinketsResponse {
  /** Every trinket the household has found, in the catalogue's order. */
  found: TrinketFind[];
  /** How many there are to find (TRINKET_TOTAL). */
  total: number;
}

/** The finds by key. */
export function findsByKey(found: ReadonlyArray<TrinketFind>): Map<TrinketKey, TrinketFind> {
  return new Map(found.map((f) => [f.key, f]));
}

// ---------- Words ----------

/** The screen's and the Profile row's name. */
export const TRINKETS_TITLE = "Trinkets";

/** The Profile row's state line and the screen's count: "5 of 25 found", "None found yet", "All 25 found". */
export function trinketCountLine(found: number, total: number = TRINKET_TOTAL): string {
  if (found <= 0) return "None found yet";
  if (found >= total) return `All ${total} found`;
  return `${found} of ${total} found`;
}

/** The find sheet's words. */
export const FIND_WORDS = {
  title: "You found something",
  /** The muted hint over the turning trinket, until the first drag. */
  hint: "Drag to turn it around",
  button: "Got it",
} as const;

/** An unfound tile's name for a screen reader, and its bubble if ever one is wanted. */
export const NOT_FOUND_YET = "Not found yet";

/** The collection's muted line while the hoard is empty. */
export const EMPTY_HOARD_LINE = "Trinkets turn up while doing chores. Keep going.";

/** "Found by Hue doing Wash the dishes" / "Found by Hue" for the viewer's muted line. */
export function foundByLine(find: Pick<TrinketFind, "found_by" | "task_title">): string {
  return find.task_title ? `Found by ${find.found_by} doing ${find.task_title}` : `Found by ${find.found_by}`;
}

/** "+10 gold" as the find sheet writes the finder's gold. */
export function findGoldLine(gold: number): string {
  return `+${gold} gold`;
}

/**
 * The push to everyone but the finder, while the flag is on: the title says it, the body is a few
 * words (docs/product.md "Push notifications"). The peon's "Ooh. Shiny." (peon-reward) plays.
 */
export function trinketPush(find: Pick<TrinketFind, "key" | "found_by">): { title: string; body: string; url: string; sound: "peon-reward" } {
  const t = trinket(find.key);
  return { title: `Trinket: ${t?.name ?? "something"}`, body: `${find.found_by} dug it up. Have a look.`, url: "/trinkets", sound: "peon-reward" };
}

/** The workshop's Dev tools button and its line (docs/trinkets.md "Trying it"). */
export const DIG_LABEL = "Dig up a trinket now";
export const DIG_BUSY = "Digging…";
/** Under the button: what came up, or that the hoard is full. */
export function digResultLine(find: Pick<TrinketFind, "key"> | null): string {
  if (!find) return `All ${TRINKET_TOTAL} found already.`;
  return `Dug up: ${trinket(find.key)?.name ?? find.key}.`;
}

// ---------- The viewer's steps ----------

/** Turning is in hard frames like a sprite (docs/product.md "Motion"): 24 steps of 15 degrees round, like the wheel. */
export const YAW_STEPS = 24;
/** Tilting: 15 degree steps, from PITCH_MIN to PITCH_MAX (-60 to 60 degrees). */
export const PITCH_STEP_DEG = 15;
export const PITCH_MIN = -4;
export const PITCH_MAX = 4;
/** The rest pose: a little from the front, looking slightly down on it. */
export const REST_YAW = 3;
export const REST_PITCH = 2;
/** Finger travel per step while dragging, in CSS px. */
export const DRAG_PX_PER_YAW = 10;
export const DRAG_PX_PER_PITCH = 16;
/** The idle turn: one step this often while nobody drags, resumed this long after a drag ends. */
export const IDLE_TURN_MS = 160;
export const IDLE_RESUME_MS = 1800;

/** Yaw wrapped into [0, YAW_STEPS). */
export function wrapYaw(step: number): number {
  return ((Math.round(step) % YAW_STEPS) + YAW_STEPS) % YAW_STEPS;
}

/** Pitch clamped to its range. */
export function clampPitch(step: number): number {
  return Math.max(PITCH_MIN, Math.min(PITCH_MAX, Math.round(step)));
}

/** The tune file for a trinket (public/audio/trinkets, scripts/make-trinket-tunes.mjs). */
export const TUNE_BASE = "/audio/trinkets/";
export function tuneFile(key: TrinketKey): string {
  return `${key}.wav`;
}
